/**
 * Contact form component.
 * Shown when the AI detects visitor frustration and requests contact details.
 */

import type { ContactFormLabels } from "../types/messages";

export interface ContactFormElements {
  container: HTMLDivElement;
  nameInput: HTMLInputElement;
  emailInput: HTMLInputElement;
}

export function createContactForm(
  onSubmit: (name: string, email: string) => void,
  labels?: ContactFormLabels,
): ContactFormElements {
  const container = document.createElement("div");
  container.className = "contact-form";

  // Header text
  const header = document.createElement("p");
  header.className = "contact-form-header";
  header.textContent =
    labels?.header ||
    "Would you like us to follow up? Leave your contact info:";
  container.appendChild(header);

  // Name input
  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.placeholder = labels?.name_placeholder || "Your name";
  nameInput.className = "contact-form-input";
  nameInput.required = true;
  container.appendChild(nameInput);

  // Email input
  const emailInput = document.createElement("input");
  emailInput.type = "email";
  emailInput.placeholder = labels?.email_placeholder || "Your email";
  emailInput.className = "contact-form-input";
  emailInput.required = true;
  container.appendChild(emailInput);

  // Error message
  const errorMessage = document.createElement("p");
  errorMessage.className = "contact-form-error";
  errorMessage.style.display = "none";
  container.appendChild(errorMessage);

  // Submit button
  const submitButton = document.createElement("button");
  submitButton.className = "contact-form-button";
  submitButton.textContent = labels?.submit || "Send";
  submitButton.type = "button";
  container.appendChild(submitButton);

  function validate(): boolean {
    const name = nameInput.value.trim();
    const email = emailInput.value.trim();

    if (!name) {
      errorMessage.textContent =
        labels?.name_required || "Please enter your name";
      errorMessage.style.display = "block";
      nameInput.focus();
      return false;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailPattern.test(email)) {
      errorMessage.textContent =
        labels?.email_invalid || "Please enter a valid email address";
      errorMessage.style.display = "block";
      emailInput.focus();
      return false;
    }

    errorMessage.style.display = "none";
    return true;
  }

  submitButton.addEventListener("click", () => {
    if (validate()) {
      nameInput.disabled = true;
      emailInput.disabled = true;
      submitButton.disabled = true;
      submitButton.textContent = labels?.sending || "Sending...";
      onSubmit(nameInput.value.trim(), emailInput.value.trim());
    }
  });

  // Submit on Enter in email field
  emailInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitButton.click();
    }
  });

  return { container, nameInput, emailInput };
}
